/**
 * MobileBookingBar Component
 *
 * Fixed bottom bar for mobile booking with Airbnb-style calendar.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ListingData } from '../hooks/useListing';
import { useAvailability } from '../hooks/useAvailability';
import { DateRangeCalendar } from '../../../components/calendar';
import { formatDate } from '../../../utils/dates';

interface MobileBookingBarProps {
  listing: ListingData;
}

export const MobileBookingBar: React.FC<MobileBookingBarProps> = ({ listing }) => {
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(false);
  const [checkIn, setCheckIn] = useState<Date | null>(null);
  const [checkOut, setCheckOut] = useState<Date | null>(null);
  const [adults, setAdults] = useState(1);
  const [children, setChildren] = useState(0);
  const [infants, setInfants] = useState(0);

  const totalGuests = adults + children;

  const { blockedDateSet } = useAvailability(listing.id);

  const handleReserve = () => {
    if (!checkIn || !checkOut) {
      setIsExpanded(true);
      return;
    }

    const params = new URLSearchParams({
      checkIn: formatDate(checkIn, 'numeric'),
      checkOut: formatDate(checkOut, 'numeric'),
      adults: String(adults),
      children: String(children),
      infants: String(infants),
    });

    navigate(`/booking/${listing.id}?${params.toString()}`);
  };

  const handleCalendarChange = (newCheckIn: Date | null, newCheckOut: Date | null) => {
    setCheckIn(newCheckIn);
    setCheckOut(newCheckOut);
  };

  return (
    <>
      {/* Collapsed Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 md:hidden z-40 safe-area-pb">
        <div className="flex items-center justify-between">
          <div>
            <span className="font-bold text-lg">${listing.pricing.nightlyRate}</span>
            <span className="text-gray-500"> / night</span>
            {listing.rating && (
              <div className="flex items-center gap-1 text-sm text-gray-500">
                <StarIcon className="w-3 h-3 fill-current" />
                <span>{listing.rating.toFixed(1)}</span>
                <span>({listing.reviewCount})</span>
              </div>
            )}
          </div>
          <button
            onClick={handleReserve}
            className="px-6 py-3 bg-primary-500 text-white font-semibold rounded-lg hover:bg-primary-600 transition-colors"
          >
            {checkIn && checkOut ? 'Reserve' : 'Check availability'}
          </button>
        </div>
      </div>

      {/* Expanded Booking Form */}
      {isExpanded && (
        <div className="fixed inset-0 z-50 md:hidden">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setIsExpanded(false)}
          />

          {/* Form */}
          <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl max-h-[90vh] overflow-y-auto safe-area-pb">
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between z-10">
              <button
                onClick={() => setIsExpanded(false)}
                className="p-2 -ml-2"
              >
                <CloseIcon className="w-5 h-5 text-gray-500" />
              </button>
              <h3 className="font-semibold">Select dates</h3>
              <div className="w-9" />
            </div>

            {/* Content */}
            <div className="p-4 space-y-4">
              {/* Price Display */}
              <div className="text-center pb-4 border-b">
                <span className="text-2xl font-bold">${listing.pricing.nightlyRate}</span>
                <span className="text-gray-500"> / night</span>
              </div>

              {/* Calendar */}
              <DateRangeCalendar
                checkIn={checkIn}
                checkOut={checkOut}
                onChange={handleCalendarChange}
                blockedDates={blockedDateSet}
                minimumStay={listing.minimumStay || 1}
              />

              {/* Guests */}
              <div className="space-y-0">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Guests
                </label>

                {/* Adults */}
                <div className="flex items-center justify-between p-3 border border-gray-300 rounded-t-lg">
                  <div>
                    <span className="text-sm font-medium text-gray-900">Adults</span>
                    <p className="text-xs text-gray-500">Age 13+</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => setAdults(Math.max(1, adults - 1))}
                      disabled={adults <= 1}
                      className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center disabled:opacity-30"
                    >
                      -
                    </button>
                    <span className="w-6 text-center text-sm">{adults}</span>
                    <button
                      type="button"
                      onClick={() => setAdults(Math.min(listing.maxGuests - children, adults + 1))}
                      disabled={totalGuests >= listing.maxGuests}
                      className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center disabled:opacity-30"
                    >
                      +
                    </button>
                  </div>
                </div>

                {/* Children */}
                <div className="flex items-center justify-between p-3 border-x border-gray-300">
                  <div>
                    <span className="text-sm font-medium text-gray-900">Children</span>
                    <p className="text-xs text-gray-500">Ages 2-12</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => setChildren(Math.max(0, children - 1))}
                      disabled={children <= 0}
                      className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center disabled:opacity-30"
                    >
                      -
                    </button>
                    <span className="w-6 text-center text-sm">{children}</span>
                    <button
                      type="button"
                      onClick={() => setChildren(Math.min(listing.maxGuests - adults, children + 1))}
                      disabled={totalGuests >= listing.maxGuests}
                      className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center disabled:opacity-30"
                    >
                      +
                    </button>
                  </div>
                </div>

                {/* Infants */}
                <div className="flex items-center justify-between p-3 border border-gray-300 rounded-b-lg">
                  <div>
                    <span className="text-sm font-medium text-gray-900">Infants</span>
                    <p className="text-xs text-gray-500">Under 2</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => setInfants(Math.max(0, infants - 1))}
                      disabled={infants <= 0}
                      className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center disabled:opacity-30"
                    >
                      -
                    </button>
                    <span className="w-6 text-center text-sm">{infants}</span>
                    <button
                      type="button"
                      onClick={() => setInfants(Math.min(5, infants + 1))}
                      disabled={infants >= 5}
                      className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center disabled:opacity-30"
                    >
                      +
                    </button>
                  </div>
                </div>

                <p className="text-xs text-gray-500 pt-1">
                  Maximum {listing.maxGuests} guests (excluding infants)
                </p>
              </div>

              {/* Reserve Button */}
              <button
                onClick={handleReserve}
                disabled={!checkIn || !checkOut}
                className="w-full py-4 bg-primary-500 text-white font-semibold rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50"
              >
                {listing.instantBooking ? 'Reserve' : 'Request to Book'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Icons
const StarIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 20 20">
    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
  </svg>
);

const CloseIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

export default MobileBookingBar;
